<template v-if="isConfiguratorLoaded">
  <vue-modal
    @before-open="setInitialData"
    @opened="opened"
    @closed="closed"
    name="customCheckerboard"
    width="500px"
    height="614px"
    :adaptive="true"
    :scrollable="false"
    :style="modalStyles"
    :click-to-close="false"
    :classes="[
      'generic-modals',
      'v--modal',
      'customs-tools',
      'customcheckerboard',
      isMainApp ? 'customcheckerboard-is-main-app' : ''
    ]"
  >
    <section :class="['customs-tools', 'customcheckerboard-wrapper']">
      <customcheckerboard-top-nav :is-main-app="isMainApp"></customcheckerboard-top-nav>
      <customcheckerboard-main-views v-bind="activeViewProperties"></customcheckerboard-main-views>
      <checkerboard-button-nav :is-main-app="isMainApp"></checkerboard-button-nav>
    </section>
  </vue-modal>
</template>

<script>
import customcheckerboardTopNav from '@/components/CustomCheckerboard/TopNav';
import customcheckerboardMainViews from '@/components/CustomCheckerboard/MainViews';
import checkerboardButtonNav from "@/components/CustomCheckerboard/ButtonNav";
import customsTools from '@/mixins/CustomsTools';
import returnQueryParams from "@/http/returnQueryParams";
import { loadPageShowEvent } from '@/utils/pageEvents';

export default {
  name: 'customcheckerboard',
  components: {
    customcheckerboardTopNav,
    customcheckerboardMainViews,
    checkerboardButtonNav
  },
  mixins: [ customsTools ],
  props: {
    isMainApp: {
      type: Boolean,
      default: true,
    }
  },
  data() {
    return {
      isConfiguratorLoaded: false,
      isStepsHeadingVisible: true,
      views: [
        'checkerboardColorSelector',
        'checkerboardSizeSelector',
        'checkerboardWarpSelector',
      ],
      selectedStyle: null,
      recipeId: null,
      selectedColor: 'color1',
    };
  },
  computed: {
    configuration() {
      return this.$store.state.utils.configuration;
    },
    activeViewProperties() {
      return { 'is-main-app': this.isMainApp };
    },
    navigation() {
      return this.$store.state.customCheckerboard.navigation;
    },
    isButtonNavSpaced() {
      return this.navigation.activeView === "checkerboardConfirmDesign";
    },
    showPreloader() {
      return this.$store.state.preloader.showPreloader;
    },
    useEcomDataLayerFormat() {
      return this.$store.getters['utils/useEcomDataLayerFormat'];
    },
    canvasActive() {
      return this.$store.getters["utils/canvasActive"];
    }
  },

  watch: {
    showPreloader() {
      // checks if preloading finished
      if (!this.showPreloader && !this.isMainApp) {
        // checks if should open the CustomCheckerboard modal
        if (
          this.$store.getters["customCheckerboard/loadAutomatically"] &&
          this.$recipe.checkUGCByType({ includeCustomCheckerboard: true })
        ) {
          this.$store.commit("customCheckerboard/SET_ACTIVE_UGC_COMPONENT", this.$recipe.getRecipeDefaultComponent());
          this.$modal.show("customCheckerboard");
        }
      }
    },
  },

  methods: {
    async setInitialData({ params = null }) {
      if (params) this.setupCaid({params});

      if (params?.vsf) this.$store.commit('utils/SET_QUERY_PARAMS', params);

      // Store source to track CTA flow
      if (params?.source) this.$store.commit('customCheckerboard/SET_SOURCE', { source: params.source });

      this.$endpoints.setConfigurationEnv();
      this.$endpoints.setEndpointEnv('customcheckerboard', this.isMainApp);

      let eventAction = 'checkerboard';
      if (this.isMainApp) eventAction += ' - external flow';
      if (this.$store.getters['customCheckerboard/isSecondComponentFlow']) eventAction += ' - second component';
      if (!this.useEcomDataLayerFormat) {
        dataLayer.push({
          'event': 'trackEvent',
          'eventCategory': 'customs',
          'eventAction': eventAction,
          'eventLabel': 'custom checkerboard entry'
        });
      }
      // sets the initial colors passed as arguments from the CTA
      if (params) {
        if (params.toolkit_options) {
          this.$store.commit("customCheckerboard/SET_CANVAS_CONFIG", {
            color1: params.toolkit_options.hex_1,
            color2: params.toolkit_options.hex_2,
          });
        }
        if (params.models) {
          this.$store.commit("customCheckerboard/SET_AVAILABLE_MODELS", params.models);
        }
        if (params.skipWarpSelector) {
          this.views.pop();
        }
        this.$store.commit("customCheckerboard/SET_SKIP_WARP_SELECTOR", params.skipWarpSelector);
      } else {
        if (!!this.configuration.enableSkipWarpSelector) {
          this.views.pop();
        }
        this.$store.commit("customCheckerboard/SET_SKIP_WARP_SELECTOR", !!this.configuration.enableSkipWarpSelector);
      }
      await this.$store.dispatch('endPoints/LOAD_LIBRARY_ENPOINTS', {
          domain: this.configuration.domain || process.env.VUE_APP_DOMAIN,
          type: 'customcheckerboard'
      }).then( () => {
        this.$store.dispatch('translations/FETCH_TRANSLATIONS', {type: 'customcheckerboard'});
        this.$store.dispatch('customCheckerboard/GET_CHECKERBOARD_SIZE');
      }).catch(err => console.error("Error when loading customcheckerboard endpoints", err));

      this.initializeNavigation();
    },
    initializeNavigation() {
      if (this.isMainApp) {
        this.views.push('checkerboardStyleSelector');
      } else {
        if (this.$store.getters['ugc/getUGCEnabledComponents'].length > 1) {
          this.views.push('checkerboardAlmostDone');
        }
        this.views.push('checkerboardConfirmDesign');
      }
      this.$store.commit("customCheckerboard/SET_VIEWS", this.views);
      this.$store.commit("customCheckerboard/SET_ACTIVE_VIEW", this.views[0] );
      if (!this.isMainApp) {
        this.$store.commit('customCheckerboard/SET_ACTIVE_UGC_COMPONENT', this.$store.state.legacyNavigation.activeComponent);
      }
    },

    goToNextStep() {
      let stepName = this.$store.state.customCheckerboard.navigation.activeView;
      let eventAction = 'checkerboard';
      if (this.isMainApp) eventAction += ' - external flow';
      if (this.$store.getters['customCheckerboard/isSecondComponentFlow']) eventAction += ' - second component';
      let eventLabel;
      if (stepName === 'checkerboardColorSelector') {
        eventLabel = 'select colors';
      } else if (stepName === 'checkerboardSizeSelector')  {
        eventLabel = 'select size';
      } else if (stepName === 'checkerboardWarpSelector') {
        eventLabel = 'select warp';
      }
      if (eventLabel && !this.useEcomDataLayerFormat) {
        dataLayer.push({
          'event': 'trackEvent',
          'eventCategory': 'customs',
          'eventAction': eventAction,
          'eventLabel': eventLabel
        });
      }
      this.$store.commit("customCheckerboard/NEXT_VIEW");
    },

    goToPreviousStep() {
      if (this.navigation.activeView === 0) {
        this.exit();
      } else {
        this.$store.commit("customCheckerboard/PREV_VIEW");
      }
    },

    exit() {
      let eventAction = 'checkerboard';
      if (this.isMainApp) eventAction += ' - external flow';
      if (this.$store.getters['customCheckerboard/isSecondComponentFlow']) eventAction += ' - second component';
      let stepName = this.$store.state.customCheckerboard.navigation.activeView;
      if (stepName === 'checkerboardColorSelector') stepName += ` ${ this.selectedColor }`;
      if (!this.useEcomDataLayerFormat) {
        dataLayer.push({
          'event': 'trackEvent',
          'eventCategory': 'customs',
          'eventAction': eventAction,
          'eventLabel': `close modal click: ${ stepName }`
        });
      }
      // resets selected base checkerboard and sticker in the component and in the vuex store
      this.$store.commit('customCheckerboard/RESET_NAVIGATION');
      this.$store.commit('customCheckerboard/RESET_CANVAS');
      if( this.$store.state.customCheckerboard.tempComponents ) {
        this.$store.commit('customCheckerboard/SET_COMPONENTS');
      }
      // resets all ugc temporary data
      this.$store.commit("ugc/tempData/CLEAR");
      this.selectedColor = 'color1';

      // hides the dialog
      this.$modal.hide("customCheckerboard");
    },

    opened() {
      if (this.canvasActive && this.isMainApp) {
        this.$iframeCommunicator.postMessage({ command: 'customsToolLoaded' });
      }
      this.adjustModalHeight();
    },

    closed() {
      this.views =  [
        'checkerboardColorSelector',
        'checkerboardSizeSelector',
        'checkerboardWarpSelector',
      ];
      this.canvasActive && this.$iframeCommunicator.postMessage({ command: 'closeIframe' });
      this.vsfActive && this.$eventBus.$emit('customsToolkitClosed');
    },
    changeSelectedColor(selectedColor) {
      this.selectedColor = selectedColor;
    },
  },
  created() {
    const urlParams = returnQueryParams();
    if (Object.keys(urlParams).length > 0) this.$store.commit('utils/SET_QUERY_PARAMS', urlParams);
    if (this.canvasActive) {
      const parentUrlParams = returnQueryParams(this.canvasActive);
      this.$store.commit('utils/SET_QUERY_PARAMS', parentUrlParams);
    }
    
    this.$eventBus.$on('customcheckerboard-go-to-previous-step', this.goToPreviousStep);
    this.$eventBus.$on('customcheckerboard-go-to-next-step', this.goToNextStep);
    this.$eventBus.$on('customcheckerboard-exit', this.exit);
    this.$eventBus.$on('customcheckerboard-change-selected-color', this.changeSelectedColor);

    if(this.isMainApp && this.vsfActive) {
      loadPageShowEvent.loadEvent();
    }
  },
  async mounted() {
    if (this.$store.getters["customCheckerboard/loadAutomatically"]) {
      const id = this.$store.getters["customCheckerboard/flowId"];
      const customsFlow = this.$store.state.utils.queryParams.customsFlow;
      if(id) {
        const el = document.getElementById(id);
        this.$modal.show("customCheckerboard", JSON.parse(el.dataset.customcheckerboardObject));
      } else if(customsFlow) {
        const toolData = await this.$store.dispatch("utils/FETCH_CUSTOMS_TOOL_DATA", {tool: customsFlow});
        if(toolData.tool === "CustomCheckerboard") this.$modal.show("customCheckerboard", toolData);
      } else {
        const el = document.getElementsByClassName("customcheckerboard-button");
        if (el[0]) this.$modal.show("customCheckerboard", JSON.parse((el[0]).dataset.customcheckerboardObject));
      }
    }

  },
  beforeDestroy() {
    this.$eventBus.$off('customcheckerboard-go-to-previous-step', this.goToPreviousStep);
    this.$eventBus.$off('customcheckerboard-go-to-next-step', this.goToNextStep);
    this.$eventBus.$off('customcheckerboard-exit', this.exit);
    this.$eventBus.$off('customcheckerboard-change-selected-color', this.changeSelectedColor);
    if(this.isMainApp && this.vsfActive) { loadPageShowEvent.removeEvent(); }

  },
}
</script>

<style lang="scss">
  .customs-tools {
    @import "~@/styles/_components/CustomCheckerboard/CustomCheckerboard.scss";
  }
</style>
